import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";

export default function ApplicationDetail() {
  const { appId } = useParams();
  const [state, setState] = useState({ application: null, source: "" });
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setErr("");
    api.get(`/ops/applications/${appId}`).then((r) => setState({ application: r.data.application, source: r.data.source }))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);

  if (err) return <div className="text-sm text-red-700" data-testid="ops-detail-error">{err}</div>;
  if (!state.application) return <div className="text-sm text-slate-500">Loading…</div>;

  const a = state.application;
  const isBuddy = state.source === "buddy_module";

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/ops/applications/${appId}/actions`, { action, note: note.trim() || null });
      toast.success(`Action ${action} recorded.`);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="ops-application-detail">
      <div className="flex items-center gap-3">
        <Link to="/ops/onboarding" className="text-sm text-slate-500">← Onboarding queue</Link>
      </div>
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-slate-500 uppercase mb-1">{a.type}</div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="ops-detail-title">
              {a.applicant_name || a.organization_name || a.user_name || "—"}
            </h1>
            <div className="text-sm text-slate-500">{a.applicant_email || a.user_email}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-1">Status</div>
            <div className="text-lg font-semibold" data-testid="ops-detail-status">{a.status}</div>
          </div>
        </div>
        {isBuddy && (
          <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
            Buddy applications remain governed by the existing Buddy admin workflow.
            {" "}<Link to="/portal/admin/buddies" className="font-semibold underline">Open Buddy admin →</Link>
          </div>
        )}
        {a.type === "volunteer" && !isBuddy && (
          <VolunteerFields a={a} />
        )}
        {a.type === "organization" && !isBuddy && (
          <OrganizationFields a={a} />
        )}
      </div>

      {!isBuddy && (
        <div className="bg-white rounded-xl border p-6 space-y-4" data-testid="ops-detail-actions">
          <h2 className="text-sm font-medium text-slate-600">Take action</h2>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Internal note (optional)" className="w-full text-sm border rounded px-3 py-2" data-testid="ops-detail-note" />
          <div className="flex flex-wrap gap-2">
            <ActionBtn action="start_review" label="Start review" enabled={a.status === "SUBMITTED"} onClick={act} busy={busy} />
            <ActionBtn action="request_info" label="Request more information" enabled={a.status === "IN_REVIEW"} onClick={act} busy={busy} />
            <ActionBtn action="start_review" label="Move back to review" enabled={a.status === "NEEDS_INFORMATION"} onClick={act} busy={busy} testId="ops-action-back-to-review" />
            <ActionBtn action="approve" label="Approve" enabled={a.status === "IN_REVIEW"} onClick={act} busy={busy} tone="emerald" />
            <ActionBtn action="decline" label="Decline" enabled={a.status === "IN_REVIEW"} onClick={act} busy={busy} tone="rose" />
            {a.type === "volunteer" && (
              <ActionBtn action="activate" label="Activate volunteer" enabled={a.status === "APPROVED"} onClick={act} busy={busy} tone="emerald" />
            )}
            {a.type === "organization" && (
              <>
                <ActionBtn action="advance_setup" label={a.status === "APPROVED" ? "Begin profile setup" : a.status === "PROFILE_SETUP" ? "Move to offerings review" : "Back to profile setup"} enabled={["APPROVED", "PROFILE_SETUP", "OFFERINGS_REVIEW"].includes(a.status)} onClick={act} busy={busy} tone="emerald" />
              </>
            )}
          </div>
          {a.status === "APPROVED" && a.type === "organization" && (
            <div className="mt-2 text-xs text-slate-500">Advancing setup creates the organisation profile. Offerings still enter existing Ecosystem governance separately.</div>
          )}
        </div>
      )}

      {!isBuddy && (a.notes || []).length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-sm font-medium text-slate-600 mb-3">Activity</h2>
          <ol className="space-y-3" data-testid="ops-detail-timeline">
            {(a.notes || []).map((n) => (
              <li key={n.id} className="text-sm border-l-2 border-slate-200 pl-3">
                <div className="font-medium text-slate-900">{n.kind} <span className="text-slate-500 font-normal">· {n.from_state} → {n.to_state}</span></div>
                {n.text && <div className="text-slate-700 mt-1">{n.text}</div>}
                <div className="text-xs text-slate-500 mt-1">{n.by_email} · {(n.at || "").slice(0, 16).replace("T", " ")}</div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ action, label, enabled, onClick, busy, tone = "slate", testId }) {
  const toneCls = {
    slate: "bg-slate-900 text-white hover:bg-slate-800",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-500",
    rose: "bg-rose-600 text-white hover:bg-rose-500",
  }[tone];
  return (
    <button
      type="button"
      disabled={!enabled || busy}
      onClick={() => onClick(action)}
      data-testid={testId || `ops-action-${action}`}
      className={`text-sm px-4 py-2 rounded font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${toneCls}`}
    >{label}</button>
  );
}

function VolunteerFields({ a }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      <Row label="Phone">{a.phone || "—"}</Row>
      <Row label="Location">{a.location || "—"}</Row>
      <Row label="Interests"><Tags v={a.interests} /></Row>
      <Row label="Availability"><Tags v={a.availability} /></Row>
      <Row label="Motivation" full>{a.motivation}</Row>
    </dl>
  );
}
function OrganizationFields({ a }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      <Row label="Organisation">{a.organization_name}</Row>
      <Row label="Type">{a.organization_type}</Row>
      <Row label="Website">{a.website ? <a href={a.website} target="_blank" rel="noreferrer" className="text-slate-900 underline">{a.website}</a> : "—"}</Row>
      <Row label="Primary contact">{a.applicant_name} · {a.applicant_email}</Row>
      <Row label="Phone">{a.phone || "—"}</Row>
      <Row label="Location">{a.location || "—"}</Row>
      <Row label="Proposed domains"><Tags v={a.proposed_domain_keys} /></Row>
      <Row label="Description" full>{a.description}</Row>
    </dl>
  );
}
function Row({ label, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <dt className="text-xs text-slate-500 mb-1 uppercase">{label}</dt>
      <dd className="text-slate-900 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}
function Tags({ v }) {
  if (!v || v.length === 0) return <>—</>;
  return <div className="flex flex-wrap gap-1">{v.map((x) => <span key={x} className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">{x}</span>)}</div>;
}
